// pages/StartTrading.tsx
import React, {
  useEffect, useState, useMemo, useRef, useCallback
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket, Search, Eye, Loader2, CheckCircle2, XCircle, RefreshCw,
  Star, StarOff, Play, Coins, Brain, Copy, Check, Maximize2
} from "lucide-react";
import { useLocation } from "wouter";
import Layout from "@/components/Layout";
import SEO from '@/components/SEO'; // SEO: start trading private -> noindex,nofollow
import ReasoningStructuredView from "@/components/ReasoningStructuredView";
import { getReasoningSectionLabels } from "@/lib/reasoningParser";
import { useAuth } from "@/hooks/useAuth";
import {
  useStartAnalysis,
  useAnalysisDetail,
  useActiveAnalyses,
  useAnalysisHistory,
  useLatestReasoning
} from "@/hooks/useAnalysis";
import { useCreditsBalance } from "@/hooks/useCredits";
// Additional imports re-added after corruption fix
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiRequest } from '@/lib/queryClient';
import { cn } from '@/lib/utils';
import { fetchJson, TransientHtmlError, TransientParseError, HttpError } from '@/lib/fetchJson';
import { getAccessToken } from '@/lib/authToken';
import {
  buildAnalysisWsUrl,
  normalizeAnalysisWsMessage,
  statusPayloadToLatestReasoning,
} from '@/lib/analysisWs';

// Minimal helpers (lost in corruption) ---------------------------------
// Expanded master symbol list (user supplied)
const ALL_SYMBOLS: string[] = [
  'ETHUSDT','BTCUSDT','LINEAUSDT','SOLUSDT','USDEUSDT','FDUSDUSDT','HOLOUSDT','XRPUSDT','BNBUSDT','DOGEUSDT','AVNTUSDT','HEMIUSDT','AVAXUSDT','PUMPUSDT','SUIUSDT','TRXUSDT','ADAUSDT','0GUSDT','ENAUSDT','WLFIUSDT','LINKUSDT','ETHFIUSDT','PEPEUSDT','NEARUSDT','PENGUUSDT','SIGNUSDT','BARDUSDT','WLDUSDT','HBARUSDT','EIGENUSDT','BBUSDT','SNXUSDT','TWTUSDT','ARBUSDT','LTCUSDT','SFPUSDT','UNIUSDT','AAVEUSDT','PAXGUSDT','DOTUSDT','SOMIUSDT','TUTUSDT','BONKUSDT','OPENUSDT','AWEUSDT','XLMUSDT','VANAUSDT','SEIUSDT','HIFIUSDT','CRVUSDT','ZROUSDT','ONDOUSDT','TRUMPUSDT','TAOUSDT','CAKEUSDT','ZKCUSDT','A2ZUSDT','APTUSDT','BERAUSDT','WIFUSDT','MEUSDT','BAKEUSDT','WUSDT','SLFUSDT','BCHUSDT','OGUSDT','OPUSDT','ZECUSDT','FILUSDT','SHIBUSDT','TIAUSDT','1000SATSUSDT','FORMUSDT','IMXUSDT','EURUSDT','PENDLEUSDT','ICPUSDT','DYDXUSDT','FLOKIUSDT','SUNUSDT','BIOUSDT','LDOUSDT','VIRTUALUSDT','BFUSDUSDT','SUSDT','PLUMEUSDT','INJUSDT','TONUSDT','HUMAUSDT','POLUSDT','ETCUSDT','GALAUSDT','XUSDUSDT','USD1USDT','KAIAUSDT','NEIROUSDT','GMXUSDT','LAUSDT','LISTAUSDT','DOLOUSDT','AEVOUSDT','FTTUSDT','RENDERUSDT','REZUSDT','PNUTUSDT','THEUSDT','SYRUPUSDT','FETUSDT','SKYUSDT','ALGOUSDT','KERNELUSDT','IOUSDT','PYTHUSDT','MITOUSDT','ARKMUSDT','SPKUSDT','WBETHUSDT','CFXUSDT','SHELLUSDT','TREEUSDT','PARTIUSDT','HYPERUSDT','ENSUSDT','ORDIUSDT','VETUSDT','MAVUSDT','WCTUSDT','OMUSDT','TSTUSDT','ATOMUSDT','RAYUSDT','WBTCUSDT','ZENUSDT','CHESSUSDT','GUNUSDT','MKRUSDT','NMRUSDT','APEUSDT','JUPUSDT','BMTUSDT','MUBARAKUSDT','MEMEUSDT','BROCCOLI714U','BABYUSDT','RUNEUSDT','BOMEUSDT','RSRUSDT','STGUSDT','STOUSDT','AIXBTUSDT','OMNIUSDT','KMNOUSDT','DOGSUSDT','PROVEUSDT','AUSDT','GRTUSDT','ALPINEUSDT','CUSDT','ARUSDT','FXSUSDT','C98USDT','RESOLVUSDT','STRKUSDT','TOWNSUSDT','BNSOLUSDT','SUSHIUSDT','PERPUSDT','TURBOUSDT','SANDUSDT','QTUMUSDT','USUALUSDT','ZKUSDT','JTOUSDT','THETAUSDT','QNTUSDT','BEAMXUSDT','INITUSDT','PEOPLEUSDT','AIUSDT','TRBUSDT','ANIMEUSDT','DEXEUSDT','NEOUSDT','EDUUSDT','YGGUSDT','SAHARAUSDT','NFPUSDT','VANRYUSDT','LPTUSDT','MANAUSDT','FISUSDT','1INCHUSDT','JASMYUSDT','JOEUSDT','CHZUSDT','1000CATUSDT','SAGAUSDT','HOOKUSDT','KAITOUSDT','USDCUSDT'
];
const formatLocal = (ts?: string | null) => ts ? new Date(ts).toLocaleString() : '—';
const formatMoney = (v: any) => {
  const n = Number(v); if (!isFinite(n)) return '—';
  if (n >= 100) return n.toFixed(2);
  if (n >= 1) return n.toFixed(3);
  return n.toFixed(5);
};
const mmss = (sec: number) => `${String(Math.floor(sec/60)).padStart(2,'0')}:${String(sec%60).padStart(2,'0')}`;
const FAILED_ANALYSIS_UI_MESSAGE = 'Analysis is temporarily unavailable. Please try again';
const LIVE_CHART_INTERVALS = [
  { label: '1m', value: '1' },
  { label: '5m', value: '5' },
  { label: '15m', value: '15' },
  { label: '30m', value: '30' },
  { label: '1h', value: '60' },
  { label: '4h', value: '240' },
  { label: '1d', value: '1D' },
  { label: '3d', value: '3D' },
] as const;

function toTradingViewSymbol(symbol?: string | null): string {
  const normalized = String(symbol ?? '').trim().toUpperCase();
  return normalized ? `BINANCE:${normalized}` : 'BINANCE:BTCUSDT';
}

function isFailedAnalysisLike(obj: any): boolean {
  if (!obj || typeof obj !== 'object') return false;
  const raw = ((obj as any).status ?? (obj as any).request_status ?? '').toString().toUpperCase();
  return raw === 'FAILED';
}

function upsertFailedAnalysisCaches(params: {
  queryClient: any;
  requestId: number;
  symbol?: string | null;
  createdAt?: string | null;
  extractRequestId?: (value: any) => number | null;
}) {
  const { queryClient, requestId, extractRequestId } = params;
  const symbol = String(params.symbol ?? '').toUpperCase();
  const createdAt = params.createdAt ?? new Date().toISOString();

  queryClient.setQueryData(["/api/analysis/active"], (prev: any) => {
    if (!Array.isArray(prev)) return prev;
    return prev.filter((entry: any) => {
      const resolvedId = extractRequestId
        ? extractRequestId(entry)
        : (typeof entry?.id === 'number' ? entry.id : typeof entry?.request_id === 'number' ? entry.request_id : null);
      return resolvedId !== requestId;
    });
  });

  queryClient.setQueryData(["/api/analysis/history"], (prev: any) => {
    const minimal = {
      id: requestId,
      analysis_request_id: requestId,
      symbol,
      status: 'FAILED',
      created_at: createdAt,
      error_message: FAILED_ANALYSIS_UI_MESSAGE,
      failure_message: FAILED_ANALYSIS_UI_MESSAGE,
      __failed_analysis: true,
    };
    if (!Array.isArray(prev)) return [minimal];
    const index = prev.findIndex((entry: any) => entry && entry.id === requestId);
    if (index === -1) return [minimal, ...prev];
    const next = prev.slice();
    next[index] = {
      ...next[index],
      ...minimal,
      symbol: minimal.symbol || next[index]?.symbol || '',
      created_at: next[index]?.created_at || minimal.created_at,
    };
    return next;
  });

  queryClient.setQueryData(["/api/analysis", requestId], (prev: any) => {
    const base = prev && typeof prev === 'object' ? prev : {};
    return {
      ...base,
      id: requestId,
      symbol: (base as any)?.symbol ?? symbol,
      status: 'FAILED',
      created_at: (base as any)?.created_at ?? createdAt,
      error_message: FAILED_ANALYSIS_UI_MESSAGE,
      failure_message: FAILED_ANALYSIS_UI_MESSAGE,
      final_signal: null,
      final_signal_id: null,
      market_health_summary: null,
    };
  });

  queryClient.setQueryData(["/api/analysis/latest-reasoning", requestId], (prev: any) => {
    const base = prev && typeof prev === 'object' ? prev : {};
    return {
      ...base,
      symbol: String((base as any)?.symbol ?? symbol),
      phase_number: Number((base as any)?.phase_number ?? 0),
      reason: '',
      time_remaining_seconds: 0,
      market_health_summary: null,
    };
  });
}

function isNoTradeLikeSignal(obj: any): boolean {
  if (!obj || typeof obj !== 'object') return false;
  if ((obj as any).no_trade === true) return true;
  const raw = ((obj as any).type ?? (obj as any).signal_type ?? (obj as any).action ?? '').toString().toUpperCase();
  return raw === 'NO_TRADE' || raw === 'NO ACTION' || raw === 'NO_ACTION';
}

function hasCoreSignalNumbers(obj: any): boolean {
  if (!obj || typeof obj !== 'object') return false;
  const referencePrice =
    (obj as any)?.reference_price ??
    (obj as any)?.referencePrice ??
    (obj as any)?.entry_price ??
    (obj as any)?.entryPrice ??
    (obj as any)?.entry;
  const primaryObjective =
    (obj as any)?.tp1 ??
    (obj as any)?.TP1 ??
    (obj as any)?.take_profit ??
    (obj as any)?.takeProfit ??
    (obj as any)?.primary_objective ??
    (obj as any)?.primaryObjective;
  const invalidation =
    (obj as any)?.invalidation_level ??
    (obj as any)?.invalidationLevel ??
    (obj as any)?.stop_loss ??
    (obj as any)?.stopLoss ??
    (obj as any)?.sl;
  return (
    Number.isFinite(Number(referencePrice)) &&
    Number.isFinite(Number(primaryObjective)) &&
    Number.isFinite(Number(invalidation))
  );
}

/* ---------- Favorites localStorage ---------- */
const FAVORITES_KEY = "fav_symbols_v1";
function loadFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(s => typeof s === "string") : [];
  } catch { return []; }
}
function saveFavorites(favs: string[]) {
  try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs)); } catch {}
}

/* ---------- Signals list hook (NEW) ---------- */
function useUserSignals() {
  // Similar strategy to useSignalsList but local to this page until unified.
  let htmlCount = 0;
  return useQuery<any[]>({
    queryKey: ['/api/signals'],
    refetchInterval: false, // WS-driven updates (no polling)
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      if (error instanceof TransientHtmlError) return failureCount < 5;
      if (error instanceof TransientParseError) return failureCount < 3;
      if (error instanceof TypeError) return failureCount < 3; // network
      return false;
    },
    queryFn: async () => {
      try {
        const data = await fetchJson<any[]>('/api/signals');
        htmlCount = 0;
        return data;
      } catch (e: any) {
        if (e instanceof TransientHtmlError) {
          htmlCount++;
          if (htmlCount < 3) {
            console.warn('[startTrading] transient HTML (suppress empty list)', e.snippet);
            return [];
          }
          throw e; // surface after threshold
        }
        if (e instanceof TransientParseError) throw e;
        if (e instanceof HttpError && e.status >= 400 && e.status < 500) throw e;
        throw e;
      }
    }
  });
}

/* ---------- Component Root ---------- */
export default function StartTrading() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { data: creditBalance } = useCreditsBalance();
  const credits = typeof creditBalance?.credits === "number"
    ? creditBalance.credits
    : (user?.credits ?? 0);

  const queryClient = useQueryClient();

  // No default symbol pre-filled; user must choose
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

  const { data: activeAnalyses = [], refetch: refetchActive } = useActiveAnalyses();
  const { data: historyAnalyses = [] } = useAnalysisHistory();
  const startAnalysis = useStartAnalysis();

  const signalsQuery = useUserSignals();
  const signals: any[] = Array.isArray(signalsQuery.data) ? signalsQuery.data : [];

  const [expandedActive, setExpandedActive] = useState<Set<number>>(new Set());
  const [expandedCompleted, setExpandedCompleted] = useState<Set<number>>(new Set());

  // WebSocket bridge (Live -> Day) state
  const wsByRequestIdRef = useRef<Map<number, WebSocket>>(new Map());
  const wsReconnectAttemptsRef = useRef<Map<number, number>>(new Map());
  const wsFallbackFetchedRef = useRef<Set<number>>(new Set());
  const [wsConnectedIds, setWsConnectedIds] = useState<Set<number>>(() => new Set());

  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [favorites, setFavorites] = useState<string[]>(() => loadFavorites());
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);

  // DAILY RESET KEY (UTC date portion)
  const [dayKey, setDayKey] = useState<string>(() => new Date().toISOString().slice(0,10));
  useEffect(() => {
    const now = new Date();
    const next = new Date(now);
    next.setUTCHours(24,0,0,0);
    const ms = next.getTime() - now.getTime();
    const t = setTimeout(() => {
      setDayKey(new Date().toISOString().slice(0,10));
      setExpandedCompleted(new Set());
    }, ms + 50);
    return () => clearTimeout(t);
  }, [dayKey]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({ title: "Authentication Required", description: "Please sign in to access analysis features.", variant: "destructive" });
      setLocation("/login");
    }
  }, [isAuthenticated, authLoading, setLocation, toast]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node) &&
          !inputRef.current?.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const FAVORITES_LIMIT = 100;
  const filteredSymbols = useMemo(() => {
    const qRaw = searchQuery.trim();
    const q = qRaw.toUpperCase();
    if (!q) {
      // Show favorites first then rest (initial view)
      const favSet = new Set(favorites);
      const rest = ALL_SYMBOLS.filter(s => !favSet.has(s));
      const base = [...favorites.filter(s=>ALL_SYMBOLS.includes(s)), ...rest];
      return (favoritesOnly ? base.filter(s => favSet.has(s)) : base).slice(0,60);
    }
    const scored: { sym: string; score: number; }[] = [];
    for (const sym of ALL_SYMBOLS) {
      const up = sym.toUpperCase();
      if (up.startsWith(q)) { scored.push({ sym, score: 100 - (up.length - q.length) }); continue; }
      const idx = up.indexOf(q); if (idx !== -1) { scored.push({ sym, score: 70 - idx }); continue; }
      let qi = 0; for (let i=0;i<up.length && qi<q.length;i++){ if (up[i]===q[qi]) qi++; }
      if (qi === q.length) scored.push({ sym, score: 40 - (up.length - q.length) });
    }
    const ordered = scored.sort((a,b)=> b.score - a.score || a.sym.localeCompare(b.sym)).map(s=>s.sym);
    const favSet = new Set(favorites);
    const favs:string[]=[]; const nonFavs:string[]=[]; for (const s of ordered) (favSet.has(s)?favs:nonFavs).push(s);
    const merged = [...favs, ...nonFavs];
    const favSet2 = new Set(favorites);
    const finalList = favoritesOnly ? merged.filter(s => favSet2.has(s)) : merged;
    return finalList.slice(0,60);
  }, [searchQuery, favorites, favoritesOnly]);

  // Remote favorites query
  interface FavoriteItem { symbol: string; created_at?: string }
  const favoritesQuery = useQuery<{ favorites: FavoriteItem[] } | null>({
    queryKey: ['/api/user/favorites'],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await apiRequest('GET','/api/user/favorites');
      return res.json();
    }
  });
  useEffect(()=> {
    if (favoritesQuery.data?.favorites) {
      const server = favoritesQuery.data.favorites.map(f=>f.symbol.toUpperCase());
      setFavorites(server);
      saveFavorites(server);
    }
  }, [favoritesQuery.data]);

  const addFavoriteMutation = useMutation({
    mutationFn: async (symbol: string) => {
      const norm = symbol.toUpperCase();
      const res = await apiRequest('POST','/api/user/favorites', { symbol: norm });
      return res.json().catch(()=>({symbol:norm}));
    },
    onError: (err, sym) => {
      // rollback
      setFavorites(prev => prev.filter(s => s !== sym));
      saveFavorites(favorites);
      const msg = String((err as any)?.message||'Add failed');
      if (msg.startsWith('401')) {
        toast({ title: 'Session expired', description: 'Please login again.', variant: 'destructive' });
        setLocation('/login');
        return;
      }
      toast({ title: 'Favorite add failed', description: msg, variant: 'destructive' });
    }
  });
  const removeFavoriteMutation = useMutation({
    mutationFn: async (symbol: string) => {
      const norm = symbol.toUpperCase();
      const res = await apiRequest('DELETE', `/api/user/favorites/${norm}`);
      return res.text();
    },
    onError: (err, sym) => {
      // rollback
      setFavorites(prev => prev.includes(sym) ? prev : [...prev, sym]);
      saveFavorites(favorites);
      const msg = String((err as any)?.message||'Remove failed');
      if (msg.startsWith('401')) {
        toast({ title: 'Session expired', description: 'Please login again.', variant: 'destructive' });
        setLocation('/login');
        return;
      }
      toast({ title: 'Favorite remove failed', description: msg, variant: 'destructive' });
    }
  });

  const toggleFavorite = useCallback((raw: string) => {
    const sym = raw.trim().toUpperCase();
    setFavorites(prev => {
      const exists = prev.includes(sym);
      if (!exists && prev.length >= FAVORITES_LIMIT) {
        toast({ title: 'Favorite limit reached', description: `Maximum ${FAVORITES_LIMIT} favorites allowed.`, variant: 'destructive' });
        return prev;
      }
      const next = exists ? prev.filter(s => s !== sym) : [...prev, sym];
      saveFavorites(next);
      setTimeout(()=> {
        if (exists) {
          removeFavoriteMutation.mutate(sym);
        } else {
          addFavoriteMutation.mutate(sym, { onError: (e:any)=> {
            if (String(e?.message||'').includes('limit')) {
              toast({ title: 'Favorite limit reached', description: `Maximum ${FAVORITES_LIMIT} favorites allowed.`, variant: 'destructive' });
            }
          }});
        }
      },0);
      return next;
    });
  }, [addFavoriteMutation, removeFavoriteMutation, toast]);

  const optimisticDecrementCredit = () => {
    queryClient.setQueryData(["/api/credits/balance"], (prev: any) =>
      prev && typeof prev.credits === "number"
        ? { ...prev, credits: Math.max(0, prev.credits - 1) }
        : prev
    );
    queryClient.setQueryData(["/api/user/profile"], (prev: any) =>
      prev && typeof prev.credits === "number"
        ? { ...prev, credits: Math.max(0, prev.credits - 1) }
        : prev
    );
  };

  const quickStartFromFavorite = async (sym: string) => {
    const norm = sym.trim().toUpperCase();
    if (!ALL_SYMBOLS.includes(norm)) {
      toast({ title: 'Invalid symbol', description: `${norm} is not available.`, variant: 'destructive' });
      return;
    }
    if (startAnalysis.isPending) return;
    if (credits <= 0) {
      toast({ title: "No tokens", description: "Insufficient tokens", variant: "destructive" });
      return;
    }
    setSelectedSymbol(norm);
    try {
      optimisticDecrementCredit();
      const res = await startAnalysis.mutateAsync({ symbol: norm });
      toast({ title: "Started", description: `Analysis for ${res.symbol} (#${res.request_id})` });
      // Proactively open WS before active list refresh
      connectAnalysisWs(res.request_id, res.symbol);
      setTimeout(() => refetchActive(), 1200);
    } catch (e: any) {
      toast({ title: "Failed", description: FAILED_ANALYSIS_UI_MESSAGE, variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/credits/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });
    }
  };

  const handleStartAnalysis = async () => {
    const norm = (selectedSymbol || '').trim().toUpperCase();
    if (!norm || !ALL_SYMBOLS.includes(norm) || credits <= 0) {
      let description: string;
      if (credits <= 0) description = 'Insufficient tokens';
      else if (!norm) description = 'Enter a symbol';
      else description = 'Symbol not in list';
      toast({ title: 'Cannot start', description, variant: 'destructive' });
      return;
    }
    try {
      optimisticDecrementCredit();
      const result = await startAnalysis.mutateAsync({ symbol: norm });
      toast({ title: 'Analysis started', description: `Started analysis for ${result.symbol} (Request #${result.request_id})` });
      // Proactively open WS before active list refresh
      connectAnalysisWs(result.request_id, result.symbol);
      setTimeout(() => refetchActive(), 1200);
    } catch (e: any) {
      toast({ title: 'Failed to start', description: FAILED_ANALYSIS_UI_MESSAGE, variant: 'destructive' });
      queryClient.invalidateQueries({ queryKey: ["/api/credits/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });
    }
  };

  const extractRequestId = useCallback((a: any): number | null => {
    if (!a || typeof a !== 'object') return null;
    const id = a.id ?? a.request_id ?? a.analysis_request_id;
    return typeof id === 'number' && id > 0 ? id : null;
  }, []);

  const fallbackFetchOnce = useCallback(async (requestId: number) => {
    if (wsFallbackFetchedRef.current.has(requestId)) return;
    wsFallbackFetchedRef.current.add(requestId);

    // Detail (single shot)
    try {
      const detailPaths = [`/api/analysis/${requestId}`, `/api/analysis/detail/${requestId}`];
      for (const p of detailPaths) {
        try {
          const res = await apiRequest('GET', p);
          const json = await res.json().catch(() => null);
          if (json && typeof json === 'object') {
            queryClient.setQueryData(["/api/analysis", requestId], json);
            break;
          }
        } catch {
          // try next path
        }
      }
    } catch {
      // ignore
    }

    // Latest reasoning (single shot)
    try {
      const reasoningPaths = [
        `/api/analysis/${requestId}/latest-reasoning?limit=900`,
        `/api/analysis/detail/${requestId}/latest-reasoning?limit=900`,
      ];
      for (const p of reasoningPaths) {
        try {
          const res = await apiRequest('GET', p);
          const json = await res.json().catch(() => null);
          if (json && typeof json === 'object') {
            queryClient.setQueryData(["/api/analysis/latest-reasoning", requestId], json);
            break;
          }
        } catch {
          // try next path
        }
      }
    } catch {
      // ignore
    }
  }, [apiRequest, queryClient]);

  const closeAnalysisWs = useCallback((requestId: number) => {
    const existing = wsByRequestIdRef.current.get(requestId);
    if (existing) {
      try {
        existing.close();
      } catch {
        // ignore
      }
      wsByRequestIdRef.current.delete(requestId);
    }
    wsReconnectAttemptsRef.current.delete(requestId);
    setWsConnectedIds((prev) => {
      if (!prev.has(requestId)) return prev;
      const next = new Set(prev);
      next.delete(requestId);
      return next;
    });
  }, []);

  const connectAnalysisWs = useCallback((requestId: number, symbolHint?: string) => {
    if (!requestId || requestId <= 0) return;
    if (wsByRequestIdRef.current.has(requestId)) return;

    const apiBaseUrl = String(import.meta.env.VITE_API_BASE_URL || '').trim();
    const token = getAccessToken();
    if (!apiBaseUrl || !token) return;

    const wsDebugEnabled = (() => {
      try {
        return import.meta.env.DEV || localStorage.getItem('debug_ws_analysis') === '1';
      } catch {
        return false;
      }
    })();

    let wsUrl: string;
    try {
      wsUrl = buildAnalysisWsUrl({ apiBaseUrl, requestId, accessToken: token });
    } catch {
      return;
    }

    const ws = new WebSocket(wsUrl);
    wsByRequestIdRef.current.set(requestId, ws);

    if (wsDebugEnabled) {
      // eslint-disable-next-line no-console
      console.log('[StartTrading][WS] connecting', { requestId, url: wsUrl.replace(/token=([^&]+)/i, 'token=***') });
    }

    ws.onopen = () => {
      wsReconnectAttemptsRef.current.set(requestId, 0);
      setWsConnectedIds((prev) => {
        if (prev.has(requestId)) return prev;
        const next = new Set(prev);
        next.add(requestId);
        return next;
      });

      if (wsDebugEnabled) {
        // eslint-disable-next-line no-console
        console.log('[StartTrading][WS] open', { requestId });
      }
    };

    ws.onmessage = (ev) => {
      const norm = normalizeAnalysisWsMessage((ev as any)?.data);
      if (!norm) return;

      const rid = norm.requestId ?? requestId;
      const payload = norm.payload ?? {};

      if (wsDebugEnabled) {
        // eslint-disable-next-line no-console
        console.log('[StartTrading][WS] message', { requestId, rid, eventType: norm.eventType, payload });
      }

      if (norm.eventType === 'STATUS') {
        // Update latest reasoning cache (Live panel)
        const latest = statusPayloadToLatestReasoning({ existing: undefined, payload, symbolHint });
        queryClient.setQueryData(["/api/analysis/latest-reasoning", rid], (prev: any) =>
          statusPayloadToLatestReasoning({ existing: prev, payload, symbolHint })
        );

        // Keep analysis detail cache in sync too (phase label / next_run_at without polling)
        const timeRemainingSecondsRaw =
          payload?.time_remaining_seconds ?? payload?.timeRemainingSeconds ?? payload?.timeRemaining;
        const timeRemainingSeconds =
          typeof timeRemainingSecondsRaw === 'number' && Number.isFinite(timeRemainingSecondsRaw)
            ? Math.max(0, Math.floor(timeRemainingSecondsRaw))
            : null;
        const computedNextRunAt =
          timeRemainingSeconds != null ? new Date(Date.now() + timeRemainingSeconds * 1000).toISOString() : undefined;

        queryClient.setQueryData(["/api/analysis", rid], (prev: any) => {
          const base = prev && typeof prev === 'object' ? prev : {};
          const prevSymbol = String((base as any)?.symbol ?? symbolHint ?? payload?.symbol ?? '').toUpperCase();
          const phaseRaw = payload?.phase ?? payload?.phase_number ?? payload?.phaseNum;
          const phaseNum = Number(phaseRaw);
          return {
            ...base,
            id: (base as any)?.id ?? rid,
            symbol: (base as any)?.symbol ?? prevSymbol,
            status: 'IN_PROGRESS',
            current_phase: Number.isFinite(phaseNum) ? phaseNum : (base as any)?.current_phase ?? latest.phase_number,
            next_run_at: (base as any)?.next_run_at ?? computedNextRunAt,
            market_health_summary: (base as any)?.market_health_summary ?? payload?.market_health_summary,
          };
        });

        // Opportunistically update active analyses list to keep countdown authoritative
        const timeRemaining =
          payload?.time_remaining_seconds ?? payload?.timeRemainingSeconds ?? payload?.timeRemaining;
        queryClient.setQueryData(["/api/analysis/active"], (prev: any) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((a: any) => {
            const aid = extractRequestId(a);
            if (aid !== rid) return a;
            return {
              ...a,
              status: a?.status || 'IN_PROGRESS',
              time_remaining_seconds:
                typeof timeRemaining === 'number' ? timeRemaining : a?.time_remaining_seconds,
            };
          });
        });
        return;
      }

      if (norm.eventType === 'RESULT_READY') {
        const request_id: number =
          payload?.request_id ?? payload?.requestId ?? rid;
        const signal_id: any = payload?.signal_id ?? payload?.signalId ?? payload?.id;
        const symbol = String(payload?.symbol ?? symbolHint ?? '').toUpperCase();
        const completedAt = payload?.completed_at ?? payload?.completedAt ?? payload?.created_at ?? payload?.createdAt;

        const signalPayload = payload?.signal;
        const signalObj =
          signalPayload && typeof signalPayload === 'object'
            ? signalPayload
            : {
                symbol,
                signal_type: typeof signalPayload === 'string' ? signalPayload : undefined,
              };

        const typeRaw =
          (signalObj as any)?.type ?? (signalObj as any)?.signal_type ?? (signalObj as any)?.action ?? payload?.signal;
        const type = typeof typeRaw === 'string' ? typeRaw.toUpperCase() : undefined;
        const confidence = payload?.confidence ?? payload?.confidence_percent ?? payload?.confidencePercent;

        // 1) Ensure day panel can render immediately (inject into signals list cache)
        const injectedSignal = {
          ...signalObj,
          id: signal_id ?? (signalObj as any)?.id ?? `ws-${request_id}`,
          analysis_request_id: request_id,
          request_id,
          symbol: (signalObj as any)?.symbol || symbol,
          type,
          signal_type: type,
          confidence_percent: (signalObj as any)?.confidence_percent ?? confidence,
          confidence,
          reasoning: (signalObj as any)?.reasoning ?? payload?.summary ?? undefined,
          summary: payload?.summary ?? undefined,
          live_reasoning: payload?.live_reasoning ?? undefined,
          created_at: (signalObj as any)?.created_at ?? completedAt,
          generated_at: (signalObj as any)?.generated_at ?? completedAt,
        };

        const injectedComplete =
          isNoTradeLikeSignal(injectedSignal) ||
          hasCoreSignalNumbers(injectedSignal) ||
          (injectedSignal.final_signal && hasCoreSignalNumbers(injectedSignal.final_signal));

        const updateSignalsList = (key: any[]) => {
          queryClient.setQueryData(key, (prev: any) => {
            const list = Array.isArray(prev) ? prev.slice() : [];
            const idx = list.findIndex((s: any) =>
              s && (s.id === injectedSignal.id || s.analysis_request_id === request_id || s.request_id === request_id)
            );
            if (idx >= 0) {
              const existing = list[idx];
              list[idx] = {
                ...existing,
                ...injectedSignal,
                // preserve nested objects if backend streams partial updates
                final_signal: {
                  ...(existing?.final_signal && typeof existing.final_signal === 'object' ? existing.final_signal : {}),
                  ...(injectedSignal?.final_signal && typeof injectedSignal.final_signal === 'object' ? injectedSignal.final_signal : {}),
                },
              };
              return list;
            }
            return [injectedSignal, ...list];
          });
        };
        if (injectedComplete) {
          updateSignalsList(['/api/signals']);
          updateSignalsList(["/api/signals/"]);
          updateSignalsList(["/api/signals"]);
        } else {
          // Avoid showing an empty duplicate card; wait for the backend to persist the full signal.
          queryClient.invalidateQueries({ queryKey: ['/api/signals'] });
          queryClient.refetchQueries({ queryKey: ['/api/signals'] });
        }

        // 2) Move analysis to history, remove from active
        queryClient.setQueryData(["/api/analysis/active"], (prev: any) =>
          Array.isArray(prev)
            ? prev.filter((a: any) => extractRequestId(a) !== request_id)
            : prev
        );
        queryClient.setQueryData(["/api/analysis/history"], (prev: any) => {
          const minimal = {
            id: request_id,
            symbol,
            status: 'COMPLETED',
            current_phase: 0,
            created_at: completedAt,
            final_signal_id: typeof signal_id === 'number' ? signal_id : undefined,
            final_signal: signalObj,
            final_signal_type: type,
            signal_type: type,
          };
          if (!Array.isArray(prev)) return [minimal];
          if (prev.some((p: any) => p && p.id === request_id)) return prev;
          return [minimal, ...prev];
        });

        // 3) Mark detail cache as completed (so Live card effect can run too)
        queryClient.setQueryData(["/api/analysis", request_id], (prev: any) => {
          const base = prev && typeof prev === 'object' ? prev : {};
          return {
            ...base,
            id: request_id,
            symbol: (base as any)?.symbol ?? symbol,
            status: 'COMPLETED',
            final_signal_id: typeof signal_id === 'number' ? signal_id : (base as any)?.final_signal_id,
            final_signal: (base as any)?.final_signal ?? signalObj,
          };
        });

        // 4) Provide a reasoning snapshot for the completed card without any polling.
        const lrText = payload?.live_reasoning ?? payload?.summary ?? (signalObj as any)?.reasoning;
        if (lrText) {
          queryClient.setQueryData(["/api/analysis/latest-reasoning", request_id], (prev: any) => {
            const prevObj = prev && typeof prev === 'object' ? prev : {};
            return {
              ...prevObj,
              symbol: String((prevObj as any)?.symbol ?? symbol),
              phase_number: Number((prevObj as any)?.phase_number ?? 3),
              reason: Array.isArray(lrText) ? lrText.join('\n') : String(lrText),
              time_remaining_seconds: 0,
            };
          });
        }

        closeAnalysisWs(request_id);
        return;
      }

      if (norm.eventType === 'ERROR') {
        upsertFailedAnalysisCaches({
          queryClient,
          requestId: rid,
          symbol: String(payload?.symbol ?? symbolHint ?? '').toUpperCase(),
          createdAt: payload?.failed_at ?? payload?.created_at ?? payload?.createdAt ?? new Date().toISOString(),
          extractRequestId,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/analysis/active"] });
        queryClient.invalidateQueries({ queryKey: ["/api/analysis/history"] });
        closeAnalysisWs(rid);
        return;
      }
    };

    ws.onerror = () => {
      // Let onclose handle fallback/reconnect
      if (wsDebugEnabled) {
        // eslint-disable-next-line no-console
        console.warn('[StartTrading][WS] error', { requestId });
      }
    };

    ws.onclose = () => {
      wsByRequestIdRef.current.delete(requestId);
      setWsConnectedIds((prev) => {
        if (!prev.has(requestId)) return prev;
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });

      if (wsDebugEnabled) {
        // eslint-disable-next-line no-console
        console.log('[StartTrading][WS] close', { requestId });
      }

      // Single-shot fallback only if analysis still in progress
      const activeNow: any = queryClient.getQueryData(["/api/analysis/active"]);
      const stillActive = Array.isArray(activeNow)
        ? activeNow.some((a: any) => extractRequestId(a) === requestId && String(a?.status).toUpperCase() === 'IN_PROGRESS')
        : activeAnalyses.some((a: any) => extractRequestId(a) === requestId && String(a?.status).toUpperCase() === 'IN_PROGRESS');
      if (stillActive) {
        fallbackFetchOnce(requestId);
      }

      // Light reconnect with small backoff (max 3 tries)
      const prevAttempts = wsReconnectAttemptsRef.current.get(requestId) ?? 0;
      const nextAttempts = prevAttempts + 1;
      wsReconnectAttemptsRef.current.set(requestId, nextAttempts);
      if (stillActive && nextAttempts <= 3) {
        const delay = nextAttempts === 1 ? 1000 : nextAttempts === 2 ? 2000 : 5000;
        window.setTimeout(() => {
          connectAnalysisWs(requestId, symbolHint);
        }, delay);
      }
    };
  }, [activeAnalyses, buildAnalysisWsUrl, closeAnalysisWs, extractRequestId, fallbackFetchOnce, queryClient]);

  const anyInProgress = activeAnalyses.some(a => a.status === "IN_PROGRESS");
  useEffect(() => {
    if (!anyInProgress) return;
    let id: number | undefined;
    const start = () => {
      if (!id && document.visibilityState === 'visible') {
        id = window.setInterval(() => { refetchActive(); }, 8000);
      }
    };
    const stop = () => { if (id) { window.clearInterval(id); id = undefined; } };
    start();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        // refresh immediately when returning
        refetchActive();
        start();
      } else {
        stop();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, [anyInProgress, refetchActive]);

  // WS: keep one socket per IN_PROGRESS analysis
  useEffect(() => {
    const inProgress = activeAnalyses
      .filter((a: any) => String(a?.status).toUpperCase() === 'IN_PROGRESS')
      .map((a: any) => ({ id: extractRequestId(a), symbol: a?.symbol }))
      .filter((x: any) => typeof x.id === 'number' && x.id > 0) as { id: number; symbol?: string }[];

    const inProgressIds = new Set(inProgress.map((x) => x.id));

    // Connect missing
    for (const item of inProgress) {
      connectAnalysisWs(item.id, item.symbol);
    }

    // Close sockets for analyses no longer active
    for (const [rid] of wsByRequestIdRef.current.entries()) {
      if (!inProgressIds.has(rid)) {
        closeAnalysisWs(rid);
      }
    }

    return;
  }, [activeAnalyses, closeAnalysisWs, connectAnalysisWs, extractRequestId]);

  // Add visibility change listener for instant refetch when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        signalsQuery.refetch();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [signalsQuery]);

  // Cleanup all sockets on unmount
  useEffect(() => {
    return () => {
      for (const [rid, ws] of wsByRequestIdRef.current.entries()) {
        try { ws.close(); } catch { /* ignore */ }
        wsByRequestIdRef.current.delete(rid);
      }
    };
  }, []);

  const onKeyDownSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown && (e.key === "ArrowDown" || e.key === "Enter")) {
      setShowDropdown(true); return;
    }
    if (!showDropdown) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIndex(i => Math.min(filteredSymbols.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIndex(i => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      const sym = filteredSymbols[highlightIndex];
      if (sym) { setSelectedSymbol(sym); setSearchQuery(sym); setShowDropdown(false); }
    } else if (e.key === "Escape") setShowDropdown(false);
  };

  // (Removed duplicate early favorites remote integration block; consolidated earlier above)

  if (authLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#00D1FF]" />
        </div>
      </Layout>
    );
  }
  if (!isAuthenticated) return null;

  // Raw signals for the day
  const daySignals = useMemo(() => (
    signals
      .filter(s => {
        const dt = (s.created_at || s.generated_at || "").slice(0,10);
        return dt === dayKey;
      })
      .sort((a,b) =>
        new Date(b.created_at || b.generated_at || 0).getTime() -
        new Date(a.created_at || a.generated_at || 0).getTime()
      )
  ), [signals, dayKey]);

  // Finished analyses (history) same day
  const finishedAnalyses = useMemo(() => (
    historyAnalyses.filter(a =>
      (a.status === "COMPLETED" || a.status === "FAILED") &&
      a.created_at &&
      a.created_at.slice(0,10) === dayKey
    )
  ), [historyAnalyses, dayKey]);

  // Merge signals + finished analyses so completed and failed items render immediately.
  const mergedDaySignals = useMemo(() => {
    const byKey = new Map<string|number, any>();
    daySignals.forEach(s => { if (s?.id != null) byKey.set(s.id, s); });
    finishedAnalyses.forEach(a => {
      if (String(a.status).toUpperCase() === 'FAILED') {
        const failedKey = `failed-analysis-${a.id}`;
        if (!byKey.has(failedKey)) {
          byKey.set(failedKey, {
            id: failedKey,
            __failed_analysis: true,
            analysis_request_id: a.id,
            symbol: a.symbol,
            created_at: a.created_at,
            status: 'FAILED',
            error_message: FAILED_ANALYSIS_UI_MESSAGE,
          });
        }
        return;
      }

      // If already have a signal referencing this analysis (by final_signal id) skip
      const existing = Array.from(byKey.values()).some((s: any) => {
        const aid = s.analysis_request_id || s.request_id || s.analysis_id;
        return aid === a.id;
      });
      if (existing) return;
      // Build synthetic item if final_signal not yet surfaced in signals API
      const fs: any = (a as any).final_signal || null;

      // If we already have a concrete final_signal object with core numbers, we can render it.
      if (fs && fs.id != null && !byKey.has(fs.id)) {
        const finalType = (a as any).final_signal_type || (a as any).signal_type || (fs as any)?.type || (fs as any)?.signal_type;
        const synthetic = { ...fs, analysis_request_id: a.id, created_at: fs.created_at || a.created_at, type: (fs as any)?.type ?? finalType };
        const ok = isNoTradeLikeSignal(synthetic) || hasCoreSignalNumbers(synthetic);
        if (ok) {
          byKey.set(fs.id, synthetic);
          return;
        }
      }

      // Otherwise, show the analysis entry (skeleton) until backend detail loads.
      const analysisOnlyId = `analysis-${a.id}`;
      if (!byKey.has(analysisOnlyId)) {
        byKey.set(analysisOnlyId, {
          id: analysisOnlyId,
          __analysis_only: true,
          analysis_request_id: a.id,
          symbol: a.symbol,
          created_at: a.created_at,
          status: a.status,
        });
      }
    });
    return Array.from(byKey.values())
      .filter(s => {
        const dt = (s.created_at || s.generated_at || '').slice(0,10);
        if (dt !== dayKey) return false;
        // Allow analysis-only skeletons (they will fill via useAnalysisDetail).
        if (s && typeof s === 'object' && ((s as any).__analysis_only || (s as any).__failed_analysis)) return true;

        // Hide incomplete signal objects (prevents empty duplicates).
        const base = (s && typeof s === 'object' && (s as any).final_signal && typeof (s as any).final_signal === 'object')
          ? (s as any).final_signal
          : s;
        return isFailedAnalysisLike(base) || isNoTradeLikeSignal(base) || hasCoreSignalNumbers(base);
      })
      .sort((a: any,b: any) =>
        new Date(b.created_at || b.generated_at || 0).getTime() -
        new Date(a.created_at || a.generated_at || 0).getTime()
      );
  }, [daySignals, finishedAnalyses, dayKey]);

  return (
    <Layout>
      <SEO title="Start Analysis | TradeSagePro" description="Generate live AI market research and analysis outputs using tokens." canonical="https://tradesagepro.com/start-trading" noindex nofollow />
      <div className="min-h-screen bg-[#142236] text-[#EAEAEA] p-5 md:p-7 relative">
        <div className="pointer-events-none absolute inset-0 opacity-[0.12] bg-[linear-gradient(120deg,#1b3352_0%,transparent_40%,transparent_60%,#1b3352_100%)]" />
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 animate-slide-in relative">
            <h1 className="group inline-block text-3xl md:text-4xl font-bold text-white tracking-tight relative">
              <span className="relative z-10">AI Market Research & Analysis</span>
              <span className="absolute left-0 -bottom-1 h-[3px] w-full bg-gradient-to-r from-[#00D1FF] via-[#4FBDF8] to-transparent rounded-full"></span>
            </h1>
            <p className="mt-3 text-sm md:text-base font-medium bg-gradient-to-r from-[#5CC8FF] via-[#8AB4FF] to-[#D38BFF] text-transparent bg-clip-text">
              Run AI-powered market research in real time with phase-by-phase validation.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-[#0B2235] to-[#132F49] border border-[#1A3E55] shadow">
                <Coins className="w-4 h-4 text-[#00D1FF]" />
                <span className="text-[12px] font-semibold tracking-wide text-[#D4E9F5]">Tokens: {credits}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Left / Middle */}
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <Card className="animate-slide-in overflow-visible">
                <CardContent className="p-5 md:p-7">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2">
                      <Rocket className="w-5 h-5 text-[#00D1FF]" /> Select Symbol
                    </h2>
                  </div>

                  <div className="relative" ref={dropdownRef}>
                    <Input
                      ref={inputRef}
                      value={searchQuery}
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase();
                        setSearchQuery(v);
                        setShowDropdown(true);
                        setHighlightIndex(0);
                      }}
                      placeholder="Search or type symbol (e.g. BTCUSDT)"
                      onFocus={() => setShowDropdown(true)}
                      onKeyDown={onKeyDownSearch}
                    />
                    <Search
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280] w-4 h-4 cursor-pointer"
                      onClick={() => setShowDropdown(s => !s)}
                    />
                    <AnimatePresence>
                      {showDropdown && (
                        <motion.div
                          key="dropdown"
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.18 }}
                          className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-xl border border-[#223047] bg-[#0D1628]/95 sm:backdrop-blur-lg shadow-2xl ring-1 ring-black/40 scrollbar-thin scrollbar-thumb-[#1E2A44] scrollbar-track-transparent"
                        >
                          {filteredSymbols.length === 0 && <div className="px-3 py-3 text-xs text-[#94A3B8]">No matches</div>}
                          {filteredSymbols.map((sym, idx) => {
                            const fav = favorites.includes(sym);
                            const active = idx === highlightIndex;
                            const q = searchQuery.trim().toUpperCase();
                            let rendered: React.ReactNode = sym;
                            if (q) {
                              const up = sym.toUpperCase();
                              const parts: React.ReactNode[] = [];
                              let i = 0; let last = 0;
                              while (i < up.length) {
                                const found = up.indexOf(q, i);
                                if (found === -1) break;
                                if (found > last) parts.push(<span key={last+':n'}>{sym.slice(last, found)}</span>);
                                parts.push(<span key={found+':hl'} className="text-[#00D1FF] font-semibold">{sym.slice(found, found + q.length)}</span>);
                                i = found + q.length; last = i;
                              }
                              if (last < sym.length) parts.push(<span key={last+':r'}>{sym.slice(last)}</span>);
                              if (parts.length) rendered = <>{parts}</>;
                            }
                            return (
                              <div
                                key={sym}
                                onMouseEnter={() => setHighlightIndex(idx)}
                                className={`flex items-center justify-between px-3 py-2 text-[12px] md:text-sm cursor-pointer transition-colors group
                                  ${active ? "bg-gradient-to-r from-[#00D1FF]/15 to-transparent" : "hover:bg-[#132034]"}
                                  ${sym === selectedSymbol ? "text-white font-semibold" : "text-[#D2D8E3]"}`}
                                onClick={async () => {
                                  const wasFav = favorites.includes(sym);
                                  setSelectedSymbol(sym); setSearchQuery(sym); setTimeout(()=>setShowDropdown(false),100);
                                  if (wasFav) {
                                    if (!startAnalysis.isPending && credits > 0) {
                                      await quickStartFromFavorite(sym);
                                    }
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-mono tracking-wide">{rendered}</span>
                                  {sym === selectedSymbol && <span className="text-[#00D1FF] text-[10px]">SELECTED</span>}
                                </div>
                                <button
                                  className="relative p-1 rounded-md text-[#64748B] hover:text-yellow-400 hover:bg-[#1E2D42] group/star"
                                  onClick={(e) => { e.stopPropagation(); toggleFavorite(sym); }}
                                >
                                  {fav ? (
                                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 drop-shadow-[0_0_4px_rgba(250,204,21,0.4)]" />
                                  ) : (
                                    <>
                                      <StarOff className="w-4 h-4 transition-opacity group-hover/star:opacity-0" />
                                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 absolute inset-1 opacity-0 group-hover/star:opacity-100 drop-shadow-[0_0_4px_rgba(250,204,21,0.35)] transition-opacity" />
                                    </>
                                  )}
                                </button>
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="mt-6">
                    <div className="mb-2 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] uppercase tracking-wider text-[#6B7789] font-semibold">Favorites</span>
                        <span className="text-[10px] text-[#5F6B7B]">({favorites.length} / 100)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 cursor-pointer select-none text-[10px] text-[#617286]">
                          <input
                            type="checkbox"
                            className="accent-[#00D1FF] w-3.5 h-3.5"
                            checked={favoritesOnly}
                            onChange={(e)=> setFavoritesOnly(e.target.checked)}
                          />
                          Favorites Only
                        </label>
                        <span className="text-[10px] text-[#4E5A6B]">Tap to auto-start</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {favorites.slice(0,3).map(sym => (
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          key={sym}
                          disabled={startAnalysis.isPending || credits <= 0}
                          onClick={() => { setSelectedSymbol(sym); setSearchQuery(sym); quickStartFromFavorite(sym); }}
                          className="relative group px-4 py-2 rounded-xl text-[11px] font-semibold tracking-wide bg-gradient-to-br from-[#10283E] via-[#0F1F33] to-[#0A1626] border border-[#1E3950] text-[#DAE4F2] shadow-sm overflow-hidden disabled:opacity-60"
                        >
                          <span className="font-mono z-10 relative">{sym}</span>
                          <span className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-[radial-gradient(circle_at_30%_30%,rgba(0,209,255,0.35),transparent_60%)] transition-opacity" />
                          <Play className="inline w-3 h-3 ml-1 text-[#00D1FF] relative z-10" />
                        </motion.button>
                      ))}
                      {favorites.length === 0 && (
                        <div className="text-[11px] text-[#5F6B7C] italic">Add favorites using star icon →</div>
                      )}
                    </div>
                  </div>

                  <div className="mt-8">
                    {credits <= 0 ? (
                      <button
                        onClick={() => setLocation('/pricing')}
                        className="group relative w-full rounded-2xl py-4 text-sm md:text-base font-semibold tracking-wide overflow-hidden bg-gradient-to-r from-[#FF6A6A] via-[#FF9040] to-[#FFCE54] text-[#1E0F0F] shadow-[0_0_0_1px_rgba(255,120,80,0.25),0_12px_32px_-8px_rgba(255,120,80,0.45)] hover:shadow-[0_0_0_1px_rgba(255,140,90,0.45),0_14px_40px_-6px_rgba(255,140,90,0.6)] transition-all"
                      >
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          <Coins className="w-5 h-5" /> Buy Tokens
                        </span>
                        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.35),transparent_70%)]" />
                      </button>
                    ) : (
                      <button
                        onClick={handleStartAnalysis}
                        disabled={startAnalysis.isPending || !selectedSymbol || credits <= 0 || !ALL_SYMBOLS.includes((selectedSymbol||'').toUpperCase())}
                        className="group relative w-full rounded-2xl py-4 text-sm md:text-base font-semibold tracking-wide overflow-hidden bg-gradient-to-r from-[#00B4FF] via-[#00D1FF] to-[#38E8FF] text-[#06131F] shadow-[0_0_0_1px_rgba(0,209,255,0.25),0_12px_32px_-8px_rgba(0,177,255,0.4)] transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-[0_0_0_1px_rgba(0,209,255,0.4),0_14px_40px_-6px_rgba(0,177,255,0.55)]"
                      >
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          {startAnalysis.isPending ? (
                            <><Loader2 className="w-5 h-5 animate-spin" /> Launching…</>
                          ) : (
                            <><span className="text-lg">⚡</span> {selectedSymbol ? `Start Analysis (${selectedSymbol})` : 'Start Analysis'} </>
                          )}
                        </span>
                        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.35),transparent_70%)]" />
                      </button>
                    )}
                  </div>

                  <AnimatePresence initial={false}>
                    {showDropdown && (
                      <motion.div
                        key="dropdown-spacer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 190, opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 140, damping: 20 }}
                        className="w-full"
                      />
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>

              <motion.div layout>
                <Card className="animate-slide-in">
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg md:text-xl font-bold">Live Analysis</h3>
                      {activeAnalyses.length > 0 && (
                        <Button
                          onClick={() => refetchActive()}
                          className="p-1.5 bg-transparent border border-[#2A3B57] hover:border-[#00D1FF]">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {activeAnalyses.length === 0 ? (
                      <div className="text-center py-10 px-4">
                        <div className="relative mx-auto mb-6 w-28 h-28">
                          <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-[#00D1FF]/20 via-[#5C60FF]/15 to-transparent animate-pulse" />
                          <div className="absolute inset-3 rounded-full bg-[#0D1626]/80 sm:backdrop-blur flex items-center justify-center border border-[#1E3148]">
                            <Brain className="w-10 h-10 text-[#00D1FF]" />
                          </div>
                        </div>
                        <p className="text-sm md:text-base text-[#8B94A7] font-medium">
                          No active analyses running.
                        </p>
                        <p className="mt-1 text-[12px] text-[#5F6B7B]">Launch one above to begin real-time AI market scanning.</p>
                      </div>
                    ) : (
                      <div className="space-y-3 md:space-y-4" style={{ contentVisibility: 'auto' }}>
                        {activeAnalyses.map(a => {
                          const id = a.id ?? (a as any).request_id ?? (a as any).analysis_request_id;
                          if (typeof id !== 'number') return null; // skip invalid entry
                          const sym = a.symbol || selectedSymbol || 'BTCUSDT';
                          const st = a.status || 'IN_PROGRESS';
                          return (
                            <LiveAnalysisCard
                              key={id}
                              analysisId={id}
                              symbol={sym}
                              status={st}
                              wsConnected={wsConnectedIds.has(id)}
                              nextRunAt={a.next_run_at}
                              createdAt={a.created_at}
                              expanded={expandedActive.has(id)}
                              onExpand={(ex) => {
                                const s = new Set(expandedActive);
                                ex ? s.add(id) : s.delete(id);
                                setExpandedActive(s);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Right */}
            <div className="lg:col-span-1 space-y-6 md:space-y-8">
              <Card className="sticky top-6 animate-slide-in">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg md:text-xl font-bold">
                      Day Analysis
                      <span className="ml-2 text-[11px] font-normal text-[#5E6B7D]">
                        (UTC {dayKey})
                      </span>
                    </h3>
          {mergedDaySignals.length > 0 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#132439] text-[#7FCDF1] border border-[#1E3950]">
            {mergedDaySignals.length}
                      </span>
                    )}
                  </div>
          {mergedDaySignals.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                      </div>
                      <p className="text-sm md:text-base text-[#8B94A7]">
                        No analyses yet today. They will appear here once finished.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 md:space-y-4" style={{ contentVisibility: 'auto' }}>
            {mergedDaySignals.slice(0, 5).map((s: any) => {
                        const sigId = s.id;
                        const analysisId = s.analysis_request_id || s.request_id || s.id;
                        return (
                          <CompletedSignalCard
                            key={`${sigId}-${analysisId}`}
                            signal={s}
                            analysisId={analysisId}
                            expanded={expandedCompleted.has(sigId)}
                            onExpand={(ex) => {
                              const ss = new Set(expandedCompleted);
                              ex ? ss.add(sigId) : ss.delete(sigId);
                              setExpandedCompleted(ss);
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-4 text-[10px] text-[#556172] italic">
                    Section resets automatically at next UTC midnight.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ---------- Live Analysis Card ---------- */
const PHASE_PLACEHOLDERS: Record<number, string[]> = {
  1: [
    "🔍 Initializing market research modules... Evaluating structure and volatility regimes.",
    "📊 AI models analyzing multi-timeframe patterns... Identifying structural zones and ranges.",
    "⚡ Processing real-time data streams... Aggregating market data points and sentiment proxies.",
    "🎯 Scanning for confluence factors... Reviewing momentum and volume profiles.",
    "🔬 Structured analysis in progress... Observing potential directional scenarios."
  ],
  2: [
    "🧠 Validation algorithms engaged... Cross-referencing technical indicators with relevant catalysts.",
    "⚖️ Assessing suggested entry zones through confluence... Pre-defined exit strategy parameters under review.",
    "🎪 Flow and positioning analysis active... Estimating bias across cohorts.",
    "🚀 Pattern recognition systems online... Validating directional hypotheses with model metrics.",
    "⭐ Market psychology review underway... Evaluating sentiment for timing context."
  ],
  3: [
    "🎖️ Final calibration... Producing analytical entry point, invalidation threshold, and projected exit levels.",
    "💎 Analysis output phase... Synthesizing data-driven insights into a research report.",
    "🏆 Analysis complete... Preparing structured output for decision support.",
    "🎯 Model validation metrics updated... Final risk assessment parameters recorded.",
    "⚡ Output consolidation in progress... Converting market intelligence into analysis insights."
  ]
};

function TradingViewAnalysisChart(props: {
  symbol: string;
  interval: string;
  heightClassName?: string;
}) {
  const { symbol, interval, heightClassName } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return;

    host.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'tradingview-widget-container h-full w-full';

    const widget = document.createElement('div');
    widget.className = 'tradingview-widget-container__widget h-full w-full';

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: toTradingViewSymbol(symbol),
      interval,
      timezone: 'Etc/UTC',
      theme: 'dark',
      style: '1',
      locale: 'en',
      enable_publishing: false,
      hide_top_toolbar: true,
      hide_legend: false,
      withdateranges: false,
      allow_symbol_change: false,
      save_image: false,
      calendar: false,
      studies: [],
      support_host: 'https://www.tradingview.com'
    });

    wrapper.appendChild(widget);
    wrapper.appendChild(script);
    host.appendChild(wrapper);

    return () => {
      host.innerHTML = '';
    };
  }, [symbol, interval]);

  return (
    <div className="rounded-xl border border-[#223047] bg-[#08111F] overflow-hidden shadow-inner">
      <div className={cn('h-[320px] w-full md:h-[380px] lg:h-[420px]', heightClassName)} ref={containerRef} />
    </div>
  );
}

function LiveAnalysisCard(props: {
  analysisId: number;
  symbol: string;
  status: string;
  wsConnected?: boolean;
  nextRunAt?: string | null;
  createdAt?: string | null;
  expanded: boolean;
  onExpand: (e: boolean) => void;
}) {
  const { analysisId, symbol, status, wsConnected, nextRunAt, createdAt, expanded, onExpand } = props;
  const { data: detail, refetch: refetchDetail } = useAnalysisDetail(analysisId);
  const { data: reasoning, refetch: refetchReasoning } = useLatestReasoning(analysisId);
  const queryClient = useQueryClient();
  const [chartInterval, setChartInterval] = useState<(typeof LIVE_CHART_INTERVALS)[number]['value']>('15');
  const [chartExpanded, setChartExpanded] = useState(false);

  const timeRemainingInitial = useMemo(() => {
    // Only show a countdown when the backend explicitly provides it.
    // Do NOT derive timers locally from next_run_at; that can create misleading countdowns.
    const raw = (reasoning as any)?.time_remaining_seconds;
    if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.floor(raw));
    return 0;
  }, [reasoning, nextRunAt]);

  const hasBackendTimer = useMemo(() => {
    const raw = (reasoning as any)?.time_remaining_seconds;
    return typeof raw === 'number' && Number.isFinite(raw);
  }, [reasoning]);

  const [countdown, setCountdown] = useState(timeRemainingInitial);
  useEffect(() => setCountdown(timeRemainingInitial), [timeRemainingInitial]);
  useEffect(() => {
    let t: number | undefined;
    const start = () => { if (!t && document.visibilityState === 'visible') t = window.setInterval(() => setCountdown(p => Math.max(0, p - 1)), 1000); };
    const stop = () => { if (t) { window.clearInterval(t); t = undefined; } };
    start();
    const onVis = () => {
      if (document.visibilityState === 'visible') start(); else stop();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  useEffect(() => {
    if (status !== "IN_PROGRESS") return;
    // If WS is connected, do not poll; WS drives cache updates.
    if (wsConnected) return;
    let id: number | undefined;
    const start = () => { if (!id && document.visibilityState === 'visible') id = window.setInterval(() => { refetchReasoning(); refetchDetail(); }, 5000); };
    const stop = () => { if (id) { window.clearInterval(id); id = undefined; } };
    start();
    const onVis = () => {
      if (document.visibilityState === 'visible') { refetchReasoning(); refetchDetail(); start(); } else stop();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, [status, wsConnected, refetchReasoning, refetchDetail]);

  // Move to history optimistically & invalidate signals when completed
  useEffect(() => {
    if (detail?.status === "COMPLETED") {
      const d: any = detail as any;
      const fs: any = d.final_signal || null;
      const deriveType = (obj: any): string | undefined => {
        if (!obj) return d.final_signal_type || d.signal_type || undefined;
        const raw = (obj.type || obj.signal_type || obj.action || '').toString().toUpperCase();
        if (raw === 'LONG' || raw === 'SHORT' || raw === 'NO_TRADE') return raw;
        if (obj.entry_price && obj.stop_loss) {
          const e = Number(obj.entry_price), sl = Number(obj.stop_loss);
          const t1 = obj.tp1 != null ? Number(obj.tp1) : (obj.take_profit != null ? Number(obj.take_profit) : NaN);
          const t2 = obj.tp2 != null ? Number(obj.tp2) : NaN;
          const bestUp = Math.max(isFinite(t1) ? t1 : -Infinity, isFinite(t2) ? t2 : -Infinity);
          const bestDn = Math.min(isFinite(t1) ? t1 : Infinity, isFinite(t2) ? t2 : Infinity);
          if (isFinite(e) && isFinite(sl)) {
            if (isFinite(bestUp) && bestUp > e && sl < e) return 'LONG';
            if (isFinite(bestDn) && bestDn < e && sl > e) return 'SHORT';
          }
        }
        return d.final_signal_type || d.signal_type || undefined;
      };
      // Do not assume NO_TRADE unless backend explicitly says so
      const apiType = d.final_signal_type || d.signal_type;
      const finalType = deriveType(fs) || (apiType === 'NO_TRADE' ? 'NO_TRADE' : undefined);

      queryClient.setQueryData(["/api/analysis/active"], (prev: any) =>
        Array.isArray(prev) ? prev.filter((x: any) => x.id !== detail.id) : prev
      );
      queryClient.setQueryData(["/api/analysis/history"], (prev: any) => {
        if (!Array.isArray(prev)) return [detail];
        if (prev.some((p: any) => p.id === detail.id)) return prev;
        const minimal = {
          id: detail.id,
          symbol: detail.symbol,
          status: detail.status,
          current_phase: detail.current_phase,
          created_at: detail.created_at,
          final_signal_id: d.final_signal_id,
          final_signal: fs || undefined,
          final_signal_type: finalType,
          signal_type: finalType,
          next_run_at: detail.next_run_at
        };
        return [minimal, ...prev];
      });
      // Push into signals cache immediately when we have a concrete final signal id
      if (fs && (fs.id != null)) {
        const fsNormalized = {
          ...fs,
          id: fs.id,
          analysis_request_id: d.id,
          symbol: fs.symbol || d.symbol,
          created_at: fs.created_at || d.created_at,
          type: fs.type || fs.signal_type || finalType,
        };

        const fsComplete = isNoTradeLikeSignal(fsNormalized) || hasCoreSignalNumbers(fsNormalized);

        if (fsComplete) {
          const updateSignalsKey = (key: any[]) => {
            queryClient.setQueryData(key, (prev: any) => {
              const list = Array.isArray(prev) ? prev.slice() : [];
              const exists = list.some((s: any) => s && (s.id === fsNormalized.id || s.analysis_request_id === d.id));
              if (exists) {
                return list.map((s: any) =>
                  s && (s.id === fsNormalized.id || s.analysis_request_id === d.id)
                    ? { ...s, ...fsNormalized }
                    : s
                );
              }
              return [fsNormalized, ...list];
            });
          };
          updateSignalsKey(["/api/signals"]);
          updateSignalsKey(["/api/signals/"]);
        } else {
          // Avoid showing an empty duplicate; wait for backend persistence.
          queryClient.invalidateQueries({ queryKey: ['/api/signals'] });
          queryClient.refetchQueries({ queryKey: ['/api/signals'] });
        }
      }
      // Refund credit if released
      if (d.credits_status === "RELEASED") {
        queryClient.setQueryData(["/api/credits/balance"], (prev: any) =>
          prev && typeof prev.credits === "number"
            ? { ...prev, credits: prev.credits + 1 }
            : prev
        );
        queryClient.setQueryData(["/api/user/profile"], (prev: any) =>
          prev && typeof prev.credits === "number"
            ? { ...prev, credits: prev.credits + 1 }
            : prev
        );
      }
      queryClient.invalidateQueries({ queryKey: ["/api/analysis/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/analysis/history"] });
      queryClient.invalidateQueries({ queryKey: ["/api/credits/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });
      // Keep signals cache, we already injected the final item above when available
      queryClient.invalidateQueries({ queryKey: ["/api/signals/"] });
      return;
    }

    if (detail?.status === 'FAILED') {
      upsertFailedAnalysisCaches({
        queryClient,
        requestId: analysisId,
        symbol: detail.symbol ?? symbol,
        createdAt: detail.created_at ?? createdAt ?? new Date().toISOString(),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/analysis/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/analysis/history"] });
    }
  }, [detail?.status, detail?.id, detail?.next_run_at, queryClient, detail]);

  const statusIcon =
    status === "FAILED" ? <XCircle className="w-4 h-4 text-red-500" /> :
    status === "COMPLETED" ? <CheckCircle2 className="w-4 h-4 text-green-500" /> :
    <Loader2 className="w-4 h-4 animate-spin text-[#00D1FF]" />;

  const showTimer = countdown > 0;
  const showReasoningButtonBase = true;

  const currentPhase: number = (detail as any)?.current_phase || 1;
  const marketHealth: any = (reasoning as any)?.market_health_summary ?? (detail as any)?.market_health_summary ?? null;

  // Phase-based animated placeholder text (shown until the backend returns current phase output)
  const placeholdersForPhase = PHASE_PLACEHOLDERS[currentPhase] || PHASE_PLACEHOLDERS[1];
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  useEffect(() => {
    setLoadingPhraseIndex(0);
  }, [currentPhase]);
  useEffect(() => {
    let iv: number | undefined;
    const start = () => {
      if (!iv && document.visibilityState === 'visible') {
        iv = window.setInterval(() => {
          setLoadingPhraseIndex((p) => (p + 1) % placeholdersForPhase.length);
        }, 2200);
      }
    };
    const stop = () => {
      if (iv) {
        window.clearInterval(iv);
        iv = undefined;
      }
    };
    start();
    const onVis = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [placeholdersForPhase.length]);

  const phaseShortLabel = useMemo(() => {
    if (currentPhase === 1) return 'Observation 1';
    if (currentPhase === 2) return 'Audit 2';
    return `Phase ${currentPhase}`;
  }, [currentPhase]);

  const summaryStatus = (marketHealth?.status && String(marketHealth.status).trim())
    ? String(marketHealth.status).trim()
    : (status === 'COMPLETED' ? 'Analysis completed' : 'Monitoring in progress');
  const summaryContext = (marketHealth?.context && String(marketHealth.context).trim())
    ? String(marketHealth.context).trim()
    : (currentPhase === 2
      ? 'Validating the thesis against current conditions.'
      : 'Observing market conditions and structural behavior.');
  const summaryExecutionNote = (marketHealth?.execution_note && String(marketHealth.execution_note).trim())
    ? String(marketHealth.execution_note).trim()
    : 'Waiting for the next validation update.';

  // Determine if existing reasoning text is stale (belongs to a previous phase)
  const reasoningPhaseRaw = (reasoning as any)?.phase_number;
  const reasoningPhase = (typeof reasoningPhaseRaw === 'number' && Number.isFinite(reasoningPhaseRaw))
    ? reasoningPhaseRaw
    : null;
  const isCompleted = (detail?.status === 'COMPLETED') || status === 'COMPLETED';
  // Strict rule: when currentPhase advances (2/3), do not show prior-phase reasoning.
  // If backend does not provide phase_number, assume it is stale once phase > 1.
  const isStaleReasoning = !isCompleted && currentPhase > 1 && (reasoningPhase == null || reasoningPhase < currentPhase);
  // Also hide reasoning once countdown hits zero while waiting for next phase update
  const waitingForNextPhase = !isCompleted && hasBackendTimer && countdown === 0 && status === 'IN_PROGRESS' && (reasoningPhase == null || reasoningPhase <= currentPhase);

  const reasoningReadyForCurrentPhase = isCompleted || (reasoningPhase != null && reasoningPhase === currentPhase);

  // Only show Market Health Summary once the backend returns results for the CURRENT phase.
  const marketHealthFromReasoning = (reasoning as any)?.market_health_summary;
  const reasoningPhaseNum = Number((reasoning as any)?.phase_number);
  const summaryPhase = marketHealthFromReasoning && Number.isFinite(reasoningPhaseNum) ? reasoningPhaseNum : null;
  const isSummaryForCurrentPhase = isCompleted || (summaryPhase != null && summaryPhase === currentPhase);

  const hasMarketHealthSummary = Boolean(
    marketHealth &&
      typeof marketHealth === 'object' &&
      String(marketHealth.status || '').trim() &&
      String(marketHealth.context || '').trim() &&
      String(marketHealth.execution_note || '').trim() &&
      isSummaryForCurrentPhase
  );

  // When we're between phase updates (timer hit zero), show the phase animation again
  // until the backend returns the next output.
  const showPhaseAnimation = !hasMarketHealthSummary || waitingForNextPhase;

  // Hide reasoning button until the current phase output is ready.
  const showReasoningButton = showReasoningButtonBase && hasMarketHealthSummary && reasoningReadyForCurrentPhase;

  // If the user expanded reasoning, but we later detect it's stale, collapse it.
  useEffect(() => {
    if (!expanded) return;
    if (showReasoningButton) return;
    onExpand(false);
  }, [expanded, showReasoningButton, onExpand]);

  // Trigger refetch shortly after phase change if we still have stale reasoning
  useEffect(() => {
    if (status !== 'IN_PROGRESS') return;
    if (isStaleReasoning || waitingForNextPhase) {
      const t = setTimeout(() => { refetchReasoning(); }, 1200);
      return () => clearTimeout(t);
    }
  }, [isStaleReasoning, waitingForNextPhase, status, refetchReasoning]);

  // Removed sticky duration/waiting display from header per user request; will show only inside expanded reasoning at the end.

  return (
  <div className="p-3 md:p-4 rounded-xl border border-[#223047] bg-gradient-to-b from-[#0D1426]/80 to-[#0B1320]/70 sm:backdrop-blur">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[12px] leading-5 md:text-[13px]">
          <div className="text-white font-bold text-sm md:text-base flex items-center gap-2">
            <span className="truncate">{String(symbol || '').toUpperCase()}</span>
            {status === 'IN_PROGRESS' && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[#1E3950] bg-[#0E2235] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8BDFFF]">
                <span className={cn('h-1.5 w-1.5 rounded-full', wsConnected ? 'bg-emerald-400 animate-pulse' : 'bg-[#00D1FF]')} />
                Live
              </span>
            )}
            {statusIcon}
          </div>
          <div className="mt-1 text-[#7F8B99] text-[11px]">
            <span>ID: #{analysisId}</span>
            <span className="opacity-60"> • </span>
            <span>{formatLocal(createdAt)}</span>
          </div>
          <div className="mt-0.5 text-[10px] md:text-[11px] text-[#A7B0C0]">
            {phaseShortLabel}
          </div>
        </div>
        <div className="text-right text-[12px] leading-5 md:text-[13px] text-[#A7B0C0]">
          {showTimer && (
            <div>Next: <span className="font-mono text-[#00D1FF]">{mmss(countdown)}</span></div>
          )}
          <div>Hold Token: 1</div>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-[#223047] bg-[#0B162A]/60 p-3">
        <div className="mb-3 flex items-start justify-between gap-3 border-b border-[#1D2B40] pb-3">
          <div className="flex flex-wrap items-center gap-2">
            {LIVE_CHART_INTERVALS.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setChartInterval(item.value)}
                className={cn(
                  'rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                  chartInterval === item.value
                    ? 'border-[#00D1FF] bg-[#00D1FF]/12 text-[#DDF8FF]'
                    : 'border-[#223047] bg-[#101B2B] text-[#8FA2B8] hover:border-[#355778] hover:text-white'
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setChartExpanded(true)}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[#223047] bg-[#101B2B] px-2.5 py-1 text-[11px] font-semibold text-[#B9C7D8] transition-colors hover:border-[#00D1FF] hover:text-white"
            aria-label="Expand live chart"
            title="Expand live chart"
          >
            <Maximize2 className="h-3.5 w-3.5" />
            Expand
          </button>
        </div>

        <TradingViewAnalysisChart
          symbol={String(symbol || '').toUpperCase()}
          interval={chartInterval}
        />
      </div>

      <Dialog open={chartExpanded} onOpenChange={setChartExpanded}>
        <DialogContent
          overlayClassName="top-16 z-40 h-[calc(100dvh-4rem)]"
          className="top-[calc(4rem+1rem)] z-40 flex h-[calc(100dvh-5.25rem)] w-[calc(100vw-0.75rem)] max-w-[min(1440px,calc(100vw-1rem))] translate-y-0 flex-col gap-0 overflow-hidden rounded-2xl border-[#223047] bg-[#08111F] p-0 text-white max-h-[calc(100dvh-5.25rem)] sm:top-[calc(4rem+1.25rem)] sm:h-[calc(100dvh-6rem)] sm:w-[calc(100vw-1.5rem)] sm:max-h-[calc(100dvh-6rem)]"
        >
          <div className="border-b border-[#1D2B40] px-4 py-3 pr-12 md:px-5 md:py-4 md:pr-14">
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold md:text-base">
              <span>{String(symbol || '').toUpperCase()}</span>
              <span className="text-[#5E7288]">·</span>
              <span className="text-[#8BDFFF]">Live Chart</span>
            </DialogTitle>
            <DialogDescription className="mt-1 text-[11px] text-[#8FA2B8] md:text-xs">
              Expanded TradingView chart for the active analysis. Selected timeframe remains synced.
            </DialogDescription>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3 pt-3 md:px-5 md:pb-5">
            <div className="mb-3 flex flex-nowrap items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {LIVE_CHART_INTERVALS.map((item) => (
                <button
                  key={`modal-${item.value}`}
                  className={cn(
                    'shrink-0 rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                    chartInterval === item.value
                      ? 'border-[#00D1FF] bg-[#00D1FF]/12 text-[#DDF8FF]'
                      : 'border-[#223047] bg-[#101B2B] text-[#8FA2B8] hover:border-[#355778] hover:text-white'
                  )}
                  type="button"
                  onClick={() => setChartInterval(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <TradingViewAnalysisChart
              symbol={String(symbol || '').toUpperCase()}
              interval={chartInterval}
              heightClassName="h-[46dvh] min-h-[320px] sm:h-[calc(100dvh-10.5rem)] sm:min-h-[470px] md:h-[calc(100dvh-9rem)] md:min-h-[600px]"
            />
          </div>
        </DialogContent>
      </Dialog>

      {!showPhaseAnimation ? (
        <div className="mt-4 rounded-xl border border-[#223047] bg-[#0B162A]/60 p-3 md:p-4">
          <div className="text-[10px] md:text-[11px] uppercase tracking-wider font-semibold text-[#8EC6E9]">Market Status</div>
          <div className="mt-1 text-white font-bold text-base md:text-lg leading-snug">{summaryStatus}</div>

          <div className="mt-3 text-[10px] md:text-[11px] uppercase tracking-wider font-semibold text-[#8EC6E9]">Market Context</div>
          <div className="mt-1 text-[12px] md:text-sm text-[#A7B0C0] leading-snug line-clamp-2">{summaryContext}</div>

          <div className="mt-3 text-[10px] md:text-[11px] uppercase tracking-wider font-semibold text-[#8EC6E9]">Execution Note</div>
          <div className="mt-1 text-[12px] md:text-sm text-[#97A6BA] italic leading-snug">{summaryExecutionNote}</div>
        </div>
      ) : (
        <div className="mt-4 text-[12px] md:text-sm text-[#EAEAEA] min-h-[18px]">
          <div>
            <div className="font-semibold text-[#00D1FF] mb-1 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#00D1FF] rounded-full animate-pulse"></span>
              Thinks...
            </div>
            <div className="animate-fade-in-out">{placeholdersForPhase[loadingPhraseIndex % placeholdersForPhase.length]}</div>
          </div>
        </div>
      )}

      {showReasoningButton && (
        <div className="mt-2 flex gap-2">
          <Button
            className="border border-[#2A3B57] bg-transparent text-[#EAEAEA] hover:border-[#00D1FF]"
            onClick={() => onExpand(!expanded)}
          >
            <Eye className="inline w-4 h-4 mr-1" />
            {expanded ? "Hide Reasoning" : "View Reasoning"}
          </Button>
        </div>
      )}

      <AnimatePresence>
        {expanded && showReasoningButton && reasoningReadyForCurrentPhase && reasoning && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 p-3 rounded-xl bg-[#0B162A] border border-[#223047] text-[12px] md:text-sm leading-relaxed space-y-3 shadow-inner"
          >
            <ReasoningStructuredView
              text={reasoning.reason}
              sectionLabels={getReasoningSectionLabels(currentPhase)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Completed Signal Card (uses signals + analysis fallback) ---------- */
function CompletedSignalCard(props: {
  analysisId?: number;
  signal?: any;
  expanded: boolean;
  onExpand: (e: boolean) => void;
}) {
  const { analysisId, signal, expanded, onExpand } = props;
  const hasAnalysis = typeof analysisId === 'number' && analysisId > 0;
  const detailQuery = useAnalysisDetail(hasAnalysis ? analysisId : 0);
  const latestReasoningQuery = useLatestReasoning(hasAnalysis ? analysisId : 0);
  const detail = detailQuery.data;
  const latestReasoning = latestReasoningQuery.data;
  const isFailedAnalysis = useMemo(() => {
    return isFailedAnalysisLike(signal) || isFailedAnalysisLike(detail);
  }, [signal, detail]);

  // A) Do not render an empty card while the backend response is still loading.
  // Only gate the initial fetch (when we don't have cached data yet).
  const waitingBackend = hasAnalysis && !isFailedAnalysis && (
    (!detail && (detailQuery.isLoading || detailQuery.isFetching)) ||
    (!latestReasoning && (latestReasoningQuery.isLoading || latestReasoningQuery.isFetching))
  );

  // Merge logic: ensure nested final_signal fields are visible immediately (prevents misclassification on first render)
  const final: any = useMemo(() => {
    const base = signal || {};
    const nested = base.final_signal && typeof base.final_signal === 'object' ? base.final_signal : null;
    const detailFs = detail && (detail as any).final_signal ? (detail as any).final_signal : null;
    // Priority: detail.final_signal > base.final_signal > base
    if (detailFs) return { ...base, ...detailFs };
    if (nested) return { ...base, ...nested };
    if (detailFs == null && !signal && detail) return detail;
    return base;
  }, [signal, detail]);

  const reasoningText: string | undefined = (latestReasoning as any)?.reason || final.reason || final.reasoning;

  const marketHealth: any = (final as any)?.market_health_summary ?? (latestReasoning as any)?.market_health_summary ?? (detail as any)?.market_health_summary ?? null;
  const summaryStatus = (marketHealth?.status && String(marketHealth.status).trim()) ? String(marketHealth.status).trim() : undefined;
  const summaryContext = (marketHealth?.context && String(marketHealth.context).trim()) ? String(marketHealth.context).trim() : undefined;
  const summaryExecutionNote = (marketHealth?.execution_note && String(marketHealth.execution_note).trim()) ? String(marketHealth.execution_note).trim() : undefined;

  // Robust NO_TRADE detection
  const isNoTrade = useMemo(() => {
    // Prefer explicit LONG/SHORT over NO_TRADE; ignore text heuristics
    const candidates = [final, signal, signal?.final_signal, detail, detail && (detail as any).final_signal].filter(Boolean);
    const types = candidates.map((c: any) => (c.type || c.signal_type || c.action || '').toString().toUpperCase());
    if (types.some(t => t === 'LONG' || t === 'SHORT')) return false;
    if (candidates.some((c: any) => c.no_trade === true)) return true;
    if (types.some(t => t === 'NO_TRADE')) return true;
    return false;
  }, [final, signal, detail]);

  function deriveEffectiveSignalType(obj: any): string | undefined {
    if (!obj) return undefined;
    const raw = (obj.type || obj.signal_type || obj.action || '').toString().toUpperCase();
    if (raw === 'NO_TRADE') return 'NO_TRADE';
    if (raw === 'LONG' || raw === 'SHORT') return raw;
    const buySell = raw.includes('BUY') ? 'LONG' : raw.includes('SELL') ? 'SHORT' : undefined;
    let fromPrices: string | undefined;
    if (obj.entry_price && obj.stop_loss) {
      const e = Number(obj.entry_price), sl = Number(obj.stop_loss);
      const t1 = obj.tp1 != null ? Number(obj.tp1) : (obj.take_profit != null ? Number(obj.take_profit) : NaN);
      const t2 = obj.tp2 != null ? Number(obj.tp2) : NaN;
      const bestUp = Math.max(isFinite(t1) ? t1 : -Infinity, isFinite(t2) ? t2 : -Infinity);
      const bestDn = Math.min(isFinite(t1) ? t1 : Infinity, isFinite(t2) ? t2 : Infinity);
      if (isFinite(e) && isFinite(sl)) {
        if (isFinite(bestUp) && bestUp > e && sl < e) fromPrices = 'LONG';
        else if (isFinite(bestDn) && bestDn < e && sl > e) fromPrices = 'SHORT';
      }
    }
    const r = (reasoningText || '').toUpperCase();
    const kw = r.includes('LONG') ? 'LONG' : r.includes('SHORT') ? 'SHORT' : undefined;
    return buySell || fromPrices || kw || undefined;
  }
  const signalType = isNoTrade ? 'NO_TRADE' : deriveEffectiveSignalType(final);
  const marketBias = signalType === 'LONG' ? 'BULLISH' : signalType === 'SHORT' ? 'BEARISH' : signalType;

  const confidencePercentRaw = (final as any)?.confidence_percent ?? (final as any)?.confidence ?? (signal as any)?.confidence_percent ?? (detail as any)?.confidence_percent;
  const confidencePercent = Number(confidencePercentRaw);
  const confidenceDisplay = useMemo(() => {
    if (!isFinite(confidencePercent)) return null;

    // Prompt-aligned ranges
    // 75–85 High, 65–74 Medium, 60–64 Low, 0 No Action, otherwise hidden.
    if (confidencePercent === 0) {
      return {
        label: 'No Action',
        className: 'text-red-200 border-red-500/40 bg-red-500/10',
        kind: 'no_action' as const,
      };
    }
    if (confidencePercent >= 75 && confidencePercent <= 85) {
      return {
        label: 'High Confidence',
        className: 'text-emerald-200 border-emerald-500/40 bg-emerald-500/10',
        kind: 'high' as const,
      };
    }
    if (confidencePercent >= 65 && confidencePercent <= 74) {
      return {
        label: 'Medium Confidence',
        className: 'text-amber-200 border-amber-500/40 bg-amber-500/10',
        kind: 'medium' as const,
      };
    }
    if (confidencePercent >= 60 && confidencePercent <= 64) {
      return {
        label: 'Low Confidence',
        className: 'text-slate-200 border-slate-500/40 bg-slate-500/10',
        kind: 'low' as const,
      };
    }
    return null;
  }, [confidencePercent]);

  const confidenceTooltip = useMemo(() => {
    const kind = confidenceDisplay?.kind;
    if (!kind) return undefined;
    if (kind === 'high') return 'High (75–85%): Strong structural alignment and clean confirmation.';
    if (kind === 'medium') return 'Medium (65–74%): Directional alignment is valid, conditions are not perfect.';
    if (kind === 'low') return 'Low (60–64%): Fragile conditions, conservative approach required.';
    if (kind === 'no_action') return 'No Action (0%): Conditions are not suitable for deployment at this time.';
    return undefined;
  }, [confidenceDisplay?.kind]);

  const isNoAction = isNoTrade || confidenceDisplay?.kind === 'no_action';

  const referencePrice =
    (final as any)?.reference_price ??
    (final as any)?.referencePrice ??
    final.entry_price ??
    (final as any)?.entryPrice ??
    (final as any)?.entry ??
    (detail as any)?.reference_price ??
    (detail as any)?.referencePrice ??
    (detail as any)?.entry_price ??
    (detail as any)?.entryPrice ??
    (detail as any)?.entry;
  const primaryObjective =
    final.tp1 ??
    (final as any)?.TP1 ??
    (final as any)?.take_profit ??
    (final as any)?.takeProfit ??
    (final as any)?.primary_objective ??
    (final as any)?.primaryObjective;
  const extendedObjective =
    final.tp2 ??
    (final as any)?.TP2 ??
    (final as any)?.extended_objective ??
    (final as any)?.extendedObjective;
  const structuralInvalidation =
    (final as any)?.invalidation_level ??
    (final as any)?.invalidationLevel ??
    final.stop_loss ??
    (final as any)?.stopLoss ??
    (final as any)?.sl ??
    (detail as any)?.invalidation_level ??
    (detail as any)?.invalidationLevel ??
    (detail as any)?.stop_loss ??
    (detail as any)?.stopLoss ??
    (detail as any)?.sl;
  const executionContextRaw =
    (final as any)?.execution_context ??
    (final as any)?.executionContext ??
    final.entry_timing ??
    (final as any)?.entryTiming ??
    'Immediate / Active Market';

  const executionContext = useMemo(() => {
    const v = String(executionContextRaw || '').trim();
    if (!v) return '—';
    if (v === 'Immediate / Active Market') return 'Immediate Market';
    return v;
  }, [executionContextRaw]);

  const rrRatio = useMemo(() => {
    const rrFromApi = Number((final as any)?.rr_ratio ?? (final as any)?.rrRatio);
    if (isFinite(rrFromApi) && rrFromApi > 0) return rrFromApi;

    const rrText =
      (final as any)?.risk_reward ??
      (final as any)?.riskReward ??
      (final as any)?.rr ??
      (final as any)?.riskRewardRatio;
    if (typeof rrText === 'string' && rrText.trim()) {
      const m = rrText.match(/([0-9]+(?:\.[0-9]+)?)/);
      if (m) {
        const parsed = Number(m[1]);
        if (isFinite(parsed) && parsed > 0) return parsed;
      }
    }

    const ref = Number(referencePrice);
    const inv = Number(structuralInvalidation);
    const tp1 = Number(primaryObjective);
    if (!isFinite(ref) || !isFinite(inv) || !isFinite(tp1)) return null;

    const bias = (marketBias || '').toString().toUpperCase();
    if (bias === 'BULLISH') {
      const risk = ref - inv;
      const reward = tp1 - ref;
      if (!(risk > 0) || !(reward > 0)) return null;
      return reward / risk;
    }
    if (bias === 'BEARISH') {
      const risk = inv - ref;
      const reward = ref - tp1;
      if (!(risk > 0) || !(reward > 0)) return null;
      return reward / risk;
    }
    return null;
  }, [final, referencePrice, structuralInvalidation, primaryObjective, marketBias]);

  const marketBiasText = useMemo(() => {
    if (isNoAction) return 'NO ACTION';
    const v = (marketBias || '').toString().toUpperCase();
    return (v === 'BULLISH' || v === 'BEARISH') ? v : '—';
  }, [marketBias, isNoAction]);

  const symbolText = String((final.symbol || signal?.symbol || '')).toUpperCase() || '—';
  const idText = `#${analysisId || final.id || '—'}`;
  const createdText = formatLocal(final.created_at || final.generated_at || (detail as any)?.created_at);

  const structureBadge = useMemo(() => {
    if (marketBiasText === 'BEARISH') return { label: 'BEARISH MARKET STRUCTURE', className: 'border-red-500/40 text-red-200 bg-[#101E2C]' };
    if (marketBiasText === 'BULLISH') return { label: 'BULLISH MARKET STRUCTURE', className: 'border-emerald-500/40 text-emerald-200 bg-[#101E2C]' };
    if (marketBiasText === 'NO ACTION') return { label: 'NO ACTION', className: 'border-red-500/40 text-red-200 bg-[#101E2C]' };
    return { label: 'MARKET STRUCTURE', className: 'border-[#2A3B57] text-[#C9D3DF] bg-[#101E2C]' };
  }, [marketBiasText]);

  const tokenBadge = useMemo(() => {
    const usedRaw = (signal as any)?.used_credits ?? (final as any)?.used_credits ?? (detail as any)?.used_credits;
    const used = Number(usedRaw);
    if (Number.isFinite(used)) {
      if (used <= 0) {
        return {
          text: 'Refund Token: 1',
          className: 'bg-[#32151A]/70 border border-[#5A2531] text-[#F5C4CC]'
        };
      }
      return {
        text: `Token Used: ${Math.max(0, Math.round(used))}`,
        className: 'bg-[#132F29]/70 border border-[#1F5A41] text-emerald-200'
      };
    }

    const creditsStatus = String((detail as any)?.credits_status ?? (signal as any)?.credits_status ?? (final as any)?.credits_status ?? '').toUpperCase();
    if (creditsStatus === 'RELEASED' || creditsStatus === 'REFUNDED') {
      return {
        text: 'Refund Token: 1',
        className: 'bg-[#32151A]/70 border border-[#5A2531] text-[#F5C4CC]'
      };
    }

    return {
      text: 'Token Used: 1',
      className: 'bg-[#132F29]/70 border border-[#1F5A41] text-emerald-200'
    };
  }, [signal, final, detail]);

  // Keep layout stable while the backend fills details.
  // If we already have the core numerical framework from the signal, render the card immediately.
  const hasCoreNumbers = isNoAction || (
    Number.isFinite(Number(referencePrice)) &&
    Number.isFinite(Number(primaryObjective)) &&
    Number.isFinite(Number(structuralInvalidation))
  );

  const showSkeleton = waitingBackend && !hasCoreNumbers;

  const hasAnyFilledContent = isNoAction || hasCoreNumbers || Boolean(
    (reasoningText && String(reasoningText).trim()) ||
    (summaryStatus && String(summaryStatus).trim()) ||
    (summaryContext && String(summaryContext).trim()) ||
    (summaryExecutionNote && String(summaryExecutionNote).trim())
  );

  const loadingSkeleton = (
    <div className="mx-auto w-full max-w-[460px] p-4 md:p-5 rounded-xl border border-[#223047] bg-gradient-to-b from-[#0D1426]/90 to-[#0B1320]/80 sm:backdrop-blur-sm animate-pulse">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-6 w-28 rounded bg-white/10" />
          <div className="h-3 w-44 rounded bg-white/5" />
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="h-6 w-24 rounded bg-white/10" />
          <div className="h-5 w-40 rounded bg-white/5" />
        </div>
      </div>

      <div className="w-full grid grid-cols-2 gap-2 mb-4 items-stretch">
        <div className="h-[54px] rounded-xl bg-white/5 border border-white/5" />
        <div className="h-[54px] rounded-xl bg-white/5 border border-white/5" />
        <div className="h-[54px] rounded-xl bg-white/5 border border-white/5" />
        <div className="h-[54px] rounded-xl bg-white/5 border border-white/5" />
        <div className="h-[54px] rounded-xl bg-white/5 border border-white/5" />
        <div className="h-[54px] rounded-xl bg-white/5 border border-white/5" />
      </div>

      <div className="mt-3 rounded-xl border border-white/5 bg-white/5 p-3">
        <div className="h-3 w-24 rounded bg-white/10" />
        <div className="mt-2 h-3 w-full rounded bg-white/5" />
        <div className="mt-4 h-3 w-28 rounded bg-white/10" />
        <div className="mt-2 h-3 w-5/6 rounded bg-white/5" />
      </div>

      <div className="mt-3 h-10 w-44 rounded-md bg-white/5 border border-white/5" />
    </div>
  );

  // Important: this must be AFTER all hooks above to keep hook order stable.
  if (showSkeleton) return loadingSkeleton;

  if (isFailedAnalysis) {
    const failedSymbol = String(signal?.symbol || (detail as any)?.symbol || '').toUpperCase() || '—';
    const failedId = `#${analysisId || (detail as any)?.id || (signal as any)?.analysis_request_id || '—'}`;
    const failedAt = formatLocal(signal?.created_at || (detail as any)?.created_at);
    return (
      <div className="mx-auto w-full max-w-[460px] p-4 md:p-5 rounded-xl border border-red-500/35 bg-gradient-to-b from-[#2B1117]/95 to-[#19090D]/90 sm:backdrop-blur-sm shadow-[0_0_0_1px_rgba(239,68,68,0.08),0_16px_36px_-18px_rgba(239,68,68,0.6)]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-red-50 font-bold text-lg md:text-xl tracking-tight truncate">{failedSymbol}</div>
            <div className="mt-1 text-[11px] text-red-200/70 font-medium flex flex-wrap gap-2">
              <span>ID: {failedId}</span>
              <span className="opacity-60">•</span>
              <span>{failedAt}</span>
            </div>
          </div>
          <div className="shrink-0 rounded-full border border-red-400/35 bg-red-500/10 p-2 text-red-300">
            <XCircle className="w-5 h-5" />
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-red-500/35 bg-red-500/10 px-4 py-4 text-red-50 font-semibold leading-relaxed">
          {FAILED_ANALYSIS_UI_MESSAGE}
        </div>
      </div>
    );
  }

  // If we somehow got an incomplete placeholder (no numbers, no summary, no reasoning),
  // hide it until the backend returns the full signal.
  if (!hasAnyFilledContent) return null;

  return (
    <div className="mx-auto w-full max-w-[460px] p-4 md:p-5 rounded-xl border border-[#223047] bg-gradient-to-b from-[#0D1426]/90 to-[#0B1320]/80 sm:backdrop-blur-sm">
      {/* 1) Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-lg md:text-xl tracking-tight truncate">{symbolText}</div>
          <div className="mt-1 text-[11px] text-[#7F8B99] font-medium flex flex-wrap gap-2">
            <span>ID: {idText}</span>
            <span className="opacity-60">•</span>
            <span>{createdText}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className={cn("px-2.5 py-1 rounded-lg text-[11px] font-semibold", tokenBadge.className)}>
            {tokenBadge.text}
          </div>
          {!isNoAction ? (
            <div className={`px-2 py-1 rounded-md text-[11px] font-semibold border ${structureBadge.className}`}>{structureBadge.label}</div>
          ) : null}
        </div>
      </div>

      {isNoAction && (
        <div className="mb-4">
          <div className="mx-auto w-full rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-center">
            <div className="text-red-300 font-extrabold tracking-wide text-base md:text-lg">NO ACTION</div>
          </div>
        </div>
      )}

      {/* 2) Numerical Framework */}
      {!isNoAction && (
        <div className="w-full grid grid-cols-2 gap-2 mb-4 items-stretch">
          <FieldBadge
            icon="📈"
            label="Data Point: Initiation"
            value={formatMoney(referencePrice)}
            color="blue"
            copyText={undefined}
          />

          <FieldBadge
            icon="🎯"
            label="Target 1 (Closer)"
            value={formatMoney(primaryObjective)}
            color="green"
            copyText={isFinite(Number(primaryObjective)) ? String(primaryObjective) : undefined}
          />

          <FieldBadge
            icon="🎯"
            label="Target 2 (Further)"
            value={formatMoney(extendedObjective)}
            color="green"
            copyText={isFinite(Number(extendedObjective)) ? String(extendedObjective) : undefined}
          />

          <FieldBadge
            icon="⛔"
            label="Risk Management Parameter"
            value={formatMoney(structuralInvalidation)}
            color="red"
            copyText={isFinite(Number(structuralInvalidation)) ? String(structuralInvalidation) : undefined}
          />

          <FieldBadge
            icon="⚖️"
            label="Pre-defined Closure Strategy (R/R)"
            value={isFinite(Number(rrRatio)) ? `${Number(rrRatio).toFixed(2)}` : '—'}
            color="purple"
            copyText={undefined}
          />

          <FieldBadge
            icon="🚀"
            label="Execution Context"
            value={executionContext || '—'}
            color="neutral"
            copyText={undefined}
          />
        </div>
      )}

      {/* 3) Confidence Layer */}
      {!isNoAction && confidenceDisplay && (
        <div className="mt-3 rounded-lg border border-[#223047] bg-[#0B162A]/60 px-2.5 py-2">
          <div
            title={confidenceTooltip}
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${confidenceDisplay.className}`}
          >
            {confidenceDisplay.label} · {Number.isFinite(confidencePercent) ? `${Math.round(confidencePercent)}%` : '—'}
          </div>
          <div className="mt-0.5 text-[9px] text-[#A7B0C0]">
            Confidence reflects structural alignment and execution conditions, not certainty.
          </div>
        </div>
      )}

      {/* 4) Market Health Summary */}
      <div className="mt-3 rounded-xl border border-[#223047] bg-[#0B162A]/60 p-2.5 md:p-3">
        <div className="text-[9px] uppercase tracking-wider font-semibold text-[#8B94A7]">MARKET STATUS</div>
        <div className="mt-1 text-[11px] md:text-[12px] text-[#D0D9E2] leading-snug">{summaryStatus || '—'}</div>

        <div className="mt-3 text-[9px] uppercase tracking-wider font-semibold text-[#8B94A7]">MARKET CONTEXT</div>
        <div className="mt-1 text-[11px] md:text-[12px] text-[#A7B0C0] leading-snug">{summaryContext || '—'}</div>

        <div className="mt-3 text-[9px] uppercase tracking-wider font-semibold text-[#8B94A7]">EXECUTION NOTE</div>
        <div className="mt-1 text-[11px] md:text-[12px] text-[#97A6BA] leading-snug">{summaryExecutionNote || '—'}</div>
      </div>

      {/* 5) View Full Reasoning */}
      {reasoningText && (
        <div className="mt-3">
          <Button
            className="border border-[#2A3B57] bg-[#0F1B2B]/40 text-white hover:border-[#00D1FF]"
            onClick={() => onExpand(!expanded)}
          >
            <Eye className="w-4 h-4 mr-2" />
            {expanded ? 'Hide Full Reasoning' : 'View Full Reasoning'}
          </Button>
        </div>
      )}

      <AnimatePresence>
        {expanded && reasoningText && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 p-4 rounded-xl bg-[#0B162A] border border-[#223047] text-[12px] md:text-sm leading-relaxed space-y-3 shadow-inner"
          >
            <ReasoningStructuredView
              text={reasoningText}
              sectionLabels={getReasoningSectionLabels(latestReasoning?.phase_number)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Small Components ---------- */
// (Removed unused StatBox component)

/* ---------- Visual Helper Components (pure UI) ---------- */

interface FieldBadgeProps {
  label: string;
  value: React.ReactNode;
  copyText?: string;
  color?: 'neutral' | 'green' | 'red' | 'purple' | 'blue' | 'cyan';
  icon?: string;
}
function FieldBadge({ label, value, color = 'neutral', icon, copyText }: FieldBadgeProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const colorMap: Record<NonNullable<FieldBadgeProps['color']>, string> = {
    neutral: 'bg-[#142332] border-[#1F3347] text-[#D0D9E2]',
    green: 'bg-[#132F29] border-[#1F5A41] text-[#CDEEDD]',
    red: 'bg-[#32151A] border-[#5A2531] text-[#F5C4CC]',
    purple: 'bg-[#241B36] border-[#3D2A5A] text-[#E3D4F9]',
    blue: 'bg-[#142841] border-[#1E4563] text-[#D0E6F5]',
    cyan: 'bg-[#123640] border-[#1F5A63] text-[#CDEFF5]',
  };
  const canCopy = typeof copyText === 'string' && copyText.trim() !== '';

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const valueKey = useMemo(() => {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    // For most uses in this page, value is a string from formatMoney/labels.
    // Fallback: avoid constant re-keys for React elements.
    return canCopy ? String(copyText) : label;
  }, [value, canCopy, copyText, label]);

  const onCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(copyText!);
      setCopied(true);
      toast({ title: 'Copied', description: `${label} copied to clipboard.` });
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard.', variant: 'destructive' });
    }
  };

  return (
    <div className={`flex flex-col gap-1 p-2 rounded-xl border ${colorMap[color]} relative overflow-hidden`}>
      <span className="text-[9px] font-semibold uppercase tracking-wide opacity-70 flex items-center gap-1 pr-5">
        {icon && <span className="text-[10px]">{icon}</span>}
        {label}
      </span>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={valueKey}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -2 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="text-[11px] font-bold font-mono leading-none"
        >
          {value}
        </motion.span>
      </AnimatePresence>
      {canCopy && (
        <button
          type="button"
          onClick={onCopy}
          aria-label={`Copy ${label}`}
          title={`Copy ${label}`}
          className="absolute top-1.5 right-1.5 p-1 rounded-md text-[#97A6BA] hover:text-white hover:bg-white/10 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      )}
      <span className="pointer-events-none absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.12),transparent_65%)]" />
    </div>
  );
}